import { useNavigate } from "react-router";

import * as navigationUtils from "src/platform/utils/navigation";

/**
 * Custom hook that provides navigation functions for the application
 * This hook should be used throughout the application for navigation instead of
 * direct navigate() calls with hardcoded paths
 */
export const useAppNavigation = () => {
  const navigate = useNavigate();

  return {
    // Root and dashboard
    goToRoot: () => navigationUtils.goToRoot(navigate),
    goToDashboard: () => navigationUtils.goToDashboard(navigate),

    // Auth
    goToLogin: () => navigationUtils.goToLogin(navigate),
    goToRegister: () => navigationUtils.goToRegister(navigate),

    // Organizations
    goToOrganizations: () => navigationUtils.goToOrganizations(navigate),
    goToCreateOrganization: () =>
      navigationUtils.goToCreateOrganization(navigate),
    goToOrganizationDetails: (alias: string) =>
      navigationUtils.goToOrganizationDetails(navigate, alias),
    goToOrganizationEdit: (alias: string) =>
      navigationUtils.goToOrganizationEdit(navigate, alias),
    goToOrganizationMembers: (alias: string) =>
      navigationUtils.goToOrganizationMembers(navigate, alias),
    goToOrganizationSettings: (alias: string) =>
      navigationUtils.goToOrganizationSettings(navigate, alias),
    goToVcsConnections: (alias: string) =>
      navigationUtils.goToVcsConnections(navigate, alias),

    // Projects
    goToProjects: (orgAlias?: string) =>
      navigationUtils.goToProjects(navigate, orgAlias),
    goToCreateProject: (orgAlias: string) =>
      navigationUtils.goToCreateProject(navigate, orgAlias),
    goToProjectDetails: (orgAlias: string, projectAlias: string) =>
      navigationUtils.goToProjectDetails(navigate, orgAlias, projectAlias),
    goToProjectEdit: (orgAlias: string, projectAlias: string) =>
      navigationUtils.goToProjectEdit(navigate, orgAlias, projectAlias),

    // Account / User
    goToUserProfile: () => navigationUtils.goToUserProfile(navigate),
    goToProfile: () => navigationUtils.goToAccountProfile(navigate),
    goToUserSettings: () => navigationUtils.goToUserSettings(navigate),
    goToAccountSettings: () => navigationUtils.goToAccountSettings(navigate),
    goToSettings: () => navigationUtils.goToSettings(navigate),

    // Applications
    goToApplications: () => navigationUtils.goToApplications(navigate),
    goToApplicationDetails: (alias: string) =>
      navigationUtils.goToApplicationDetails(navigate, alias),
    goToProjectApplications: (orgAlias: string, projectAlias: string) =>
      navigationUtils.goToProjectApplications(navigate, orgAlias, projectAlias),
    goToProjectApplicationDetails: (
      orgAlias: string,
      projectAlias: string,
      appAlias: string,
    ) =>
      navigationUtils.goToProjectApplicationDetails(
        navigate,
        orgAlias,
        projectAlias,
        appAlias,
      ),
    goToCreateApplication: (orgAlias: string, projectAlias: string) =>
      navigationUtils.goToCreateApplication(navigate, orgAlias, projectAlias),

    // Stacks
    goToStackDetails: (
      orgAlias: string,
      projectAlias: string,
      sysAlias: string,
    ) =>
      navigationUtils.goToStackDetails(
        navigate,
        orgAlias,
        projectAlias,
        sysAlias,
      ),
    goToStackEdit: (orgAlias: string, projectAlias: string, sysAlias: string) =>
      navigationUtils.goToStackEdit(navigate, orgAlias, projectAlias, sysAlias),
    goToStackServices: (
      orgAlias: string,
      projectAlias: string,
      sysAlias: string,
    ) =>
      navigationUtils.goToStackServices(
        navigate,
        orgAlias,
        projectAlias,
        sysAlias,
      ),
    goToAdoptStack: (orgAlias: string, projectAlias: string) =>
      navigationUtils.goToAdoptStack(navigate, orgAlias, projectAlias),

    // Generic navigation
    navigateTo: (path: string, options?: { replace?: boolean }) =>
      navigationUtils.navigateTo(navigate, path, options),
  };
};
